import { GlassCard } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { MapPin, Users, User as UserIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { type InstalledApp } from "@/hooks/useDevices";
import DeviceCardHeader from "./device/DeviceCardHeader";
import DeviceCardInfo from "./device/DeviceCardInfo";
import { toast } from "sonner";
import * as devicesApi from "@/services/devicesApi";
import { useCompactMode } from "@/hooks/useCompactMode";
import { highlightMatch } from "@/utils/highlightMatch";

interface Device {
  id: string;
  name: string;
  os: string;
  status: string;
  lastSeen: string;
  ip: string;
  installed_apps: InstalledApp[];
  battery_level?: number | null;
  battery_status?: string | null;
  serial_number?: string | null;
  department?: string;
  location?: string;
  username?: string | null;
  fullName?: string | null;
  userEmail?: string | null;
}

const DeviceCard = ({ device, searchQuery = "" }: { device: Device; searchQuery?: string }) => {
  const navigate = useNavigate();
  const { cx } = useCompactMode();

  const triggerHeartbeat = async () => {
    try {
      await devicesApi.checkInDevice({ uuid: device.id });
      toast.success('Device check-in successful');
    } catch (error) {
      console.error('Error in triggerHeartbeat:', error);
      toast.error('Failed to trigger device heartbeat');
    }
  };

  const handleUnenroll = async () => {
    try {
      await devicesApi.bulkUnenroll({ deviceIds: [device.id] });
      toast.success('Device unenrolled successfully');
    } catch (error) {
      console.error('Error unenrolling device:', error);
      toast.error('Failed to unenroll device');
    }
  };

  return (
    <GlassCard variant="default" className={cx("p-6", "p-4")}>
      <DeviceCardHeader
        name={device.name}
        status={device.status}
        onCheckIn={triggerHeartbeat}
        onUnenroll={handleUnenroll}
      />
      <DeviceCardInfo
        os={device.os}
        osType={device.os.split(' ')[0]}
        deviceName={device.name}
        lastSeen={device.lastSeen}
        ip={device.ip}
        serialNumber={device.serial_number}
        batteryLevel={device.battery_level}
        batteryStatus={device.battery_status}
        installedApps={device.installed_apps}
      />
      
      {(device.username || device.fullName || device.userEmail) && (
        <div className={cx("mt-4 pt-4 border-t border-border", "mt-3 pt-3 border-t border-border")}>
          <div className="flex items-start text-sm">
            <UserIcon className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              {device.username && <div className="font-medium truncate">{highlightMatch(device.username, searchQuery)}</div>}
              {device.fullName && device.fullName !== device.username && (
                <div className="text-xs text-muted-foreground truncate">{highlightMatch(device.fullName, searchQuery)}</div>
              )}
              {device.userEmail && <div className="text-muted-foreground truncate">{highlightMatch(device.userEmail, searchQuery)}</div>}
            </div>
          </div>
        </div>
      )}

      {(device.department || device.location) && (
        <div className={cx("mt-4 pt-4 border-t border-border space-y-2", "mt-3 pt-3 border-t border-border space-y-1.5")}>
          {device.department && (
            <div className="flex items-center text-sm text-muted-foreground">
              <Users className="h-4 w-4 mr-2" />
              {device.department}
            </div>
          )}
          {device.location && (
            <div className="flex items-center text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 mr-2" />
              {device.location}
            </div>
          )}
        </div>
      )}
      
      <div className={cx("mt-4", "mt-3")}>
        <Button variant="outline" className="w-full" onClick={() => navigate(`/devices/${device.id}`)}>
          View Details
        </Button>
      </div>
    </GlassCard>
  );
};

export default DeviceCard;
